import { useMemo } from 'react';

// 4 cosmic palettes for 12 Jyotirlingas (cycling)
const JP = [
  { s1:'#040115',s2:'#0c0530',t:'#1a0c48',a:'#D4AF37',g:'#7C3AED',m:'#E8D5FF',l:'#C4B5FD' }, // cosmic violet
  { s1:'#001020',s2:'#002540',t:'#004060',a:'#87CEEB',g:'#0EA5E9',m:'#BAE6FD',l:'#7DD3FC' }, // deep ocean
  { s1:'#150503',s2:'#2a0a05',t:'#401008',a:'#FF9500',g:'#DC2626',m:'#FED7AA',l:'#FDBA74' }, // sacred fire
  { s1:'#030a0a',s2:'#061a1a',t:'#0a2e2e',a:'#6EE7B7',g:'#059669',m:'#A7F3D0',l:'#6EE7B7' }, // forest divine
];

function getJyotirlIngaTerrainType(state) {
  if (['Uttarakhand'].includes(state)) return 'mountain';
  if (['Gujarat','Tamil Nadu'].includes(state)) return 'coastal';
  if (['Andhra Pradesh','Maharashtra'].includes(state)) return 'forest';
  if (['Uttar Pradesh','Madhya Pradesh'].includes(state)) return 'river';
  return 'hills';
}

function buildJyotirlIngaTerrain(type, W, H) {
  const ty = H * 0.58;
  switch(type) {
    case 'mountain':
      return `M0,${ty} Q${W*.15},${ty-22} ${W*.28},${ty-12} Q${W*.4},${ty-36} ${W*.52},${ty-20} Q${W*.66},${ty-40} ${W*.78},${ty-18} Q${W*.9},${ty-8} ${W},${ty-15} L${W},${H} L0,${H}Z`;
    case 'coastal':
      return `M0,${ty+7} Q${W*.25},${ty+1} ${W*.5},${ty+9} Q${W*.75},${ty+16} ${W},${ty+7} L${W},${H} L0,${H}Z`;
    case 'forest':
      return `M0,${ty} L${W*.1},${ty-15} L${W*.2},${ty-6} L${W*.32},${ty-20} L${W*.42},${ty-8} L${W*.52},${ty-24} L${W*.63},${ty-10} L${W*.74},${ty-18} L${W*.85},${ty-6} L${W*.93},${ty-14} L${W},${ty} L${W},${H} L0,${H}Z`;
    case 'river':
      return `M0,${ty+4} Q${W*.28},${ty-8} ${W*.52},${ty+4} Q${W*.76},${ty+14} ${W},${ty+4} L${W},${H} L0,${H}Z`;
    default:
      return `M0,${ty+5} Q${W*.26},${ty-15} ${W*.52},${ty+5} Q${W*.78},${ty+18} ${W},${ty+5} L${W},${H} L0,${H}Z`;
  }
}

// Star grid for jyotirlinga (slightly more stars - cosmic theme)
const J_STARS = [
  [8,8],[32,6],[58,11],[82,5],[106,14],[118,20],
  [20,22],[46,18],[72,26],[96,20],[12,34],[40,30],
  [66,38],[90,32],[115,38],[26,46],[52,42],[78,48],
];

// ── Shiva Linga silhouette ───────────────────────────────────────────────────
function ShivaLinga({ cx, by, fill, accent }) {
  return (
    <g>
      {/* Pitha (base platform) */}
      <rect x={cx-22} y={by-8} width={44} height={8} rx={2} fill={fill} />
      {/* Argha / Yoni (sacred tray) */}
      <ellipse cx={cx} cy={by-10} rx={17} ry={5.5} fill={fill} opacity={.85} />
      {/* Linga body */}
      <rect x={cx-9} y={by-48} width={18} height={38} rx={9} fill={fill} opacity={.92} />
      {/* Linga top hemisphere */}
      <ellipse cx={cx} cy={by-48} rx={9} ry={6} fill={fill} opacity={.9} />
      {/* Jaladhari (water spout) */}
      <path d={`M${cx+9},${by-20} Q${cx+18},${by-18} ${cx+22},${by-10}`}
        fill="none" stroke={fill} strokeWidth={2} opacity={.7} />
      {/* Sacred glow within linga */}
      <rect x={cx-5} y={by-46} width={10} height={34} rx={5} fill={accent} opacity={.1} />
      <ellipse cx={cx} cy={by-28} rx={4} ry={8} fill={accent} opacity={.12} />
    </g>
  );
}

// Trishul (trident) floating beside linga
function Trishul({ cx, by, accent }) {
  const tx = cx + 28, ty = by - 42;
  return (
    <g opacity={.55}>
      <line x1={tx} y1={ty} x2={tx} y2={ty+24} stroke={accent} strokeWidth={1.5} />
      {/* Three prongs */}
      <line x1={tx} y1={ty} x2={tx} y2={ty-12} stroke={accent} strokeWidth={1.8} />
      <line x1={tx-5} y1={ty} x2={tx-5} y2={ty-8} stroke={accent} strokeWidth={1.2} />
      <line x1={tx+5} y1={ty} x2={tx+5} y2={ty-8} stroke={accent} strokeWidth={1.2} />
      {/* Prong tips */}
      <circle cx={tx} cy={ty-12} r={1.2} fill={accent} />
      <circle cx={tx-5} cy={ty-8} r={1} fill={accent} />
      <circle cx={tx+5} cy={ty-8} r={1} fill={accent} />
      {/* Cross bar */}
      <line x1={tx-6} y1={ty-2} x2={tx+6} y2={ty-2} stroke={accent} strokeWidth={1} />
    </g>
  );
}

// Nandi silhouette (small, beside base)
function Nandi({ cx, by, fill }) {
  const nx = cx - 30, ny = by - 15;
  return (
    <g opacity={.5}>
      {/* Body */}
      <ellipse cx={nx} cy={ny} rx={10} ry={7} fill={fill} />
      {/* Head */}
      <circle cx={nx+10} cy={ny-4} r={5.5} fill={fill} />
      {/* Horns */}
      <path d={`M${nx+8},${ny-9} Q${nx+6},${ny-16} ${nx+10},${ny-14}`} fill="none" stroke={fill} strokeWidth={1.5} />
      <path d={`M${nx+12},${ny-9} Q${nx+14},${ny-15} ${nx+10},${ny-14}`} fill="none" stroke={fill} strokeWidth={1.5} />
      {/* Legs */}
      <line x1={nx-5} y1={ny+7} x2={nx-5} y2={ny+14} stroke={fill} strokeWidth={2} />
      <line x1={nx+2} y1={ny+7} x2={nx+2} y2={ny+14} stroke={fill} strokeWidth={2} />
    </g>
  );
}

// Flowing Ganga at top
function GangaFlow({ cx, accent }) {
  return (
    <g opacity={.45}>
      <path d={`M${cx-18},28 Q${cx-12},22 ${cx-6},28 Q${cx},22 ${cx+6},28 Q${cx+12},22 ${cx+18},28`}
        fill="none" stroke={accent} strokeWidth={1.2} />
      <path d={`M${cx-15},33 Q${cx-9},27 ${cx-3},33 Q${cx+3},27 ${cx+9},33 Q${cx+15},27 ${cx+21},33`}
        fill="none" stroke={accent} strokeWidth={.8} opacity={.6} />
    </g>
  );
}

const JPARTS = [
  { dx:-18, delay:'0s',    dur:'3.2s' },
  { dx:  0, delay:'1.15s', dur:'3.6s' },
  { dx: 18, delay:'0.6s',  dur:'2.9s' },
];

export default function JyotirlIngaIllustration({ jyotirlingaId = 1, state = '' }) {
  const id  = jyotirlingaId;
  const pal = JP[(id - 1) % 4];
  const terrType = useMemo(() => getJyotirlIngaTerrainType(state), [state]);

  const W = 120, H = 160;
  const cx = 60, by = 118;
  const uid = `jl${id}`;

  const floatDur = `${3.8 + (id % 4) * 0.42}s`;
  const pulseDur = `${3.0 + (id % 3) * 0.36}s`;
  const twOff    = (id % 6) * 0.35;

  const terrPath = buildJyotirlIngaTerrain(terrType, W, H);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg"
      style={{ width:'100%', height:'100%', display:'block' }} aria-hidden="true">

      <defs>
        <linearGradient id={`${uid}-sky`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={pal.s1} />
          <stop offset="100%" stopColor={pal.s2} />
        </linearGradient>
        <radialGradient id={`${uid}-halo`} cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor={pal.g} stopOpacity=".45" />
          <stop offset="60%"  stopColor={pal.g} stopOpacity=".12" />
          <stop offset="100%" stopColor={pal.g} stopOpacity="0"   />
        </radialGradient>
        <radialGradient id={`${uid}-linga-glow`} cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor={pal.l} stopOpacity=".6" />
          <stop offset="100%" stopColor={pal.l} stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Sky */}
      <rect width={W} height={H} fill={`url(#${uid}-sky)`} />

      {/* Moon */}
      <circle cx={W-17} cy={14} r={9}   fill={pal.m} opacity={.88} />
      <circle cx={W-13} cy={11} r={7.2} fill={pal.s1} />
      <circle cx={W-17} cy={14} r={16}  fill={pal.m} opacity={.06} />

      {/* Stars — more numerous for cosmic feel */}
      {J_STARS.map(([sx, sy], i) => (
        <circle key={i} cx={sx} cy={sy} r={.8} fill={pal.l} opacity={.6}
          style={{
            animation:`peeth-twinkle ${(1.4+(i*0.28+twOff)%2.0).toFixed(1)}s ease-in-out ${((i*0.19+twOff*.4)%1.7).toFixed(2)}s infinite`
          }} />
      ))}

      {/* Occasional brighter star */}
      {[1,4,7,10].map(i => (
        <circle key={i} cx={J_STARS[i][0]} cy={J_STARS[i][1]} r={1.4} fill={pal.a} opacity={.7}
          style={{ animation:`peeth-twinkle 2s ease-in-out ${i*0.35}s infinite` }} />
      ))}

      {/* Terrain */}
      <path d={terrPath} fill={pal.t} />

      {terrType === 'mountain' && (
        <>
          <path d={`M${W*.39},${H*.58-34} Q${W*.405},${H*.58-40} ${W*.42},${H*.58-34} Z`} fill="white" opacity={.6} />
          <path d={`M${W*.505},${H*.58-18} Q${W*.52},${H*.58-24} ${W*.535},${H*.58-18} Z`} fill="white" opacity={.5} />
        </>
      )}
      {terrType === 'river' && (
        <path d={`M${W*.06},${H*.59} Q${W*.35},${H*.583} ${W*.62},${H*.59} Q${W*.8},${H*.596} ${W*.94},${H*.588}`}
          fill="none" stroke={pal.a} strokeWidth={.7} opacity={.22} />
      )}
      {terrType === 'coastal' && (
        <path d={`M0,${H*.586} Q${W*.25},${H*.577} ${W*.5},${H*.586} Q${W*.75},${H*.595} ${W},${H*.586}`}
          fill="none" stroke="#87CEEB" strokeWidth={.7} opacity={.25} />
      )}

      {/* Ganga flow decoration at top */}
      <GangaFlow cx={cx} accent={pal.a} />

      {/* Divine halo */}
      <ellipse cx={cx} cy={by-38} rx={42} ry={52}
        fill={`url(#${uid}-halo)`}
        style={{ animation:`peeth-pulse ${pulseDur} ease-in-out infinite` }} />

      {/* Linga glow (inner light) */}
      <ellipse cx={cx} cy={by-28} rx={16} ry={26}
        fill={`url(#${uid}-linga-glow)`}
        style={{ animation:`peeth-pulse ${pulseDur} ease-in-out .4s infinite` }} />

      {/* Nandi — companion */}
      <Nandi cx={cx} by={by} fill={pal.t} />

      {/* Trishul */}
      <g style={{ animation:`peeth-float ${floatDur} ease-in-out .8s infinite`, transformOrigin:`${cx+28}px ${by-42}px` }}>
        <Trishul cx={cx} by={by} accent={pal.a} />
      </g>

      {/* Shiva Linga (floating) */}
      <g style={{
        animation: `peeth-float ${floatDur} ease-in-out infinite`,
        transformOrigin: `${cx}px ${by}px`,
      }}>
        <ShivaLinga cx={cx} by={by} fill={pal.t} accent={pal.a} />
      </g>

      {/* Rising sacred particles */}
      {JPARTS.map((p, i) => (
        <circle key={i} cx={cx+p.dx} cy={by-4} r={1.4} fill={pal.a}
          style={{ animation:`peeth-rise ${p.dur} ease-out ${p.delay} infinite` }} />
      ))}

      {/* Sacred Om at top */}
      <text x={cx} y={22} textAnchor="middle"
        fontFamily="serif" fontSize={11} fill={pal.a} opacity={.65}
        style={{ animation:`peeth-pulse ${pulseDur} ease-in-out .6s infinite` }}>
        ॐ नमः शिवाय
      </text>

    </svg>
  );
}
